import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  MessageCircle,
  Shield,
  Sparkles,
  Code,
} from 'lucide-react';

export function FeaturesSection() {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const showcaseRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: false, margin: '-100px' });
  const [activeFeature, setActiveFeature] = useState(0);

  // Mouse tracking for interactive effects
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 25, stiffness: 150 };
  const mouseXSpring = useSpring(mouseX, springConfig);
  const mouseYSpring = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (showcaseRef.current) {
        const rect = showcaseRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        mouseX.set(x);
        mouseY.set(y);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  const features = [
    {
      icon: MessageCircle,
      title: t('landing.features.multiChannel.title'),
      description: t('landing.features.multiChannel.description'),
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/20',
      borderColor: 'border-emerald-200 dark:border-emerald-800',
    },
    {
      icon: Zap,
      title: t('landing.features.lightning.title'),
      description: t('landing.features.lightning.description'),
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950/20',
      borderColor: 'border-orange-200 dark:border-orange-800',
    },
    {
      icon: Shield,
      title: t('landing.features.dataControl.title'),
      description: t('landing.features.dataControl.description'),
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    {
      icon: Code,
      title: t('landing.features.developerFriendly.title'),
      description: t('landing.features.developerFriendly.description'),
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
    },
  ];

  // Auto-play animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <section id="features" ref={containerRef} className="relative py-16 md:py-20 overflow-hidden">
      {/* OpenClaw Logo Background */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <motion.img
          src="/openclaw.svg"
          alt=""
          className="w-[45%] h-auto opacity-[0.02] dark:opacity-[0.03]"
          animate={{ rotate: -360 }}
          transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-emerald-50/20 dark:via-emerald-950/10 to-background" />

      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            {t('landing.features.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            {t('landing.features.titleStart')}{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
              {t('landing.features.titleHighlight')}
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('landing.features.subtitle')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Feature Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            {features.map((feature, idx) => {
              const FeatureIcon = feature.icon;
              const isActive = activeFeature === idx;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + idx * 0.1 }}
                  onMouseEnter={() => setActiveFeature(idx)}
                  className="cursor-pointer"
                >
                  <motion.div
                    className={`
                      relative rounded-2xl border-2 p-6 transition-all duration-300
                      ${isActive
                        ? `${feature.borderColor} ${feature.bgColor} shadow-xl`
                        : 'border-border bg-background/50 hover:border-emerald-500/30'
                      }
                    `}
                    animate={isActive ? {
                      scale: 1.02,
                      x: 8,
                    } : {}}
                  >
                    {/* Active Indicator */}
                    {isActive && (
                      <motion.div
                        className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-l-2xl"
                        layoutId="activeIndicator"
                      />
                    )}

                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <motion.div
                        className={`
                          w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color}
                          flex items-center justify-center flex-shrink-0
                        `}
                        animate={isActive ? {
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0]
                        } : {}}
                        transition={{ duration: 0.5 }}
                      >
                        <FeatureIcon className="w-7 h-7 text-white" />
                      </motion.div>

                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {feature.description}
                        </p>
                      </div>

                      {/* Animated Dots */}
                      {isActive && (
                        <div className="flex gap-1.5 items-center">
                          {[0, 1].map((i) => (
                            <motion.div
                              key={i}
                              className="w-2 h-2 rounded-full bg-emerald-500"
                              animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.5, 1, 0.5]
                              }}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                delay: i * 0.3
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Right - Visual Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            <div
              ref={showcaseRef}
              className="aspect-[4/3] rounded-2xl border-2 overflow-hidden relative backdrop-blur-sm"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, rgba(20, 184, 166, 0.03) 40%, rgba(59, 130, 246, 0.02) 100%)',
                borderColor: 'rgba(16, 185, 129, 0.2)',
              }}
            >
              {/* Animated Background Grid */}
              <motion.div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, rgba(16, 185, 129, 0.15) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(16, 185, 129, 0.15) 1px, transparent 1px)
                  `,
                  backgroundSize: '40px 40px',
                }}
                animate={{
                  backgroundPosition: ['0px 0px', '40px 40px']
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear'
                }}
              />

              {/* Animated Gradient Orbs */}
              <motion.div
                className="absolute w-64 h-64 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                }}
                animate={{
                  x: [-50, 50, -50],
                  y: [-50, 50, -50],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              />
              <motion.div
                className="absolute w-48 h-48 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)',
                  filter: 'blur(35px)',
                  right: 0,
                  bottom: 0,
                }}
                animate={{
                  x: [50, -50, 50],
                  y: [50, -50, 50],
                  scale: [1, 1.3, 1],
                }}
                transition={{
                  duration: 12,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1
                }}
              />

              {/* Floating Elements */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="relative"
                  animate={{
                    y: [0, -20, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                >
                  {/* Glow Ring Around Logo */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  >
                    <div
                      className="w-40 h-40 rounded-full"
                      style={{
                        background: 'radial-gradient(circle, transparent 40%, rgba(16, 185, 129, 0.2) 50%, transparent 60%)',
                        filter: 'blur(10px)'
                      }}
                    />
                  </motion.div>

                  {/* Center Logo - Draggable and Interactive */}
                  <motion.img
                    src="/openclaw.svg"
                    alt="OpenClaw"
                    className="w-32 h-32 cursor-grab active:cursor-grabbing relative z-10"
                    style={{
                      filter: 'drop-shadow(0 10px 30px rgba(16, 185, 129, 0.4))'
                    }}
                    drag
                    dragConstraints={{
                      top: -100,
                      left: -100,
                      right: 100,
                      bottom: 100,
                    }}
                    dragElastic={0.2}
                    dragTransition={{ bounceStiffness: 300, bounceDamping: 20 }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{
                      rotate: 360,
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                      scale: { duration: 3, repeat: Infinity }
                    }}
                  />

                  {/* Connection Lines */}
                  <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%' }}>
                    {features.map((_, idx) => {
                      const angle = (idx * 360) / features.length;
                      const x1 = '50%';
                      const y1 = '50%';
                      const x2 = `${50 + Math.cos((angle * Math.PI) / 180) * 20}%`;
                      const y2 = `${50 + Math.sin((angle * Math.PI) / 180) * 20}%`;

                      return (
                        <motion.line
                          key={`line-${idx}`}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke="rgba(16, 185, 129, 0.2)"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{
                            pathLength: activeFeature === idx ? 1 : 0.5,
                            opacity: activeFeature === idx ? 0.6 : 0.2,
                            strokeWidth: activeFeature === idx ? 3 : 2
                          }}
                          transition={{ duration: 0.5 }}
                        />
                      );
                    })}
                  </svg>

                  {/* Orbiting Feature Icons - Draggable */}
                  {features.map((feature, idx) => {
                    const angle = (idx * 360) / features.length;
                    const FeatureIcon = feature.icon;

                    return (
                      <motion.div
                        key={idx}
                        className="absolute top-1/2 left-1/2 cursor-grab active:cursor-grabbing"
                        style={{
                          x: '-50%',
                          y: '-50%'
                        }}
                        drag
                        dragConstraints={{
                          top: -150,
                          left: -150,
                          right: 150,
                          bottom: 150,
                        }}
                        dragElastic={0.3}
                        dragTransition={{ bounceStiffness: 200, bounceDamping: 15 }}
                        whileHover={{ scale: 1.2, rotate: 15 }}
                        whileTap={{ scale: 0.8 }}
                        animate={{
                          x: [
                            '-50%',
                            `${Math.cos((angle * Math.PI) / 180) * 100 - 50}%`,
                            '-50%'
                          ],
                          y: [
                            '-50%',
                            `${Math.sin((angle * Math.PI) / 180) * 100 - 50}%`,
                            '-50%'
                          ],
                          scale: activeFeature === idx ? [1, 1.3, 1] : 1
                        }}
                        transition={{
                          duration: 8,
                          repeat: Infinity,
                          delay: idx * 0.5,
                          ease: 'easeInOut'
                        }}
                        onHoverStart={() => setActiveFeature(idx)}
                      >
                        <motion.div
                          className={`
                            w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color}
                            flex items-center justify-center shadow-lg
                            ${activeFeature === idx ? 'ring-4 ring-emerald-500/50' : ''}
                          `}
                          animate={activeFeature === idx ? {
                            rotate: [0, 360],
                          } : {}}
                          transition={{
                            duration: 2,
                            ease: 'linear'
                          }}
                        >
                          <FeatureIcon className="w-6 h-6 text-white" />
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>

              {/* Floating Particles - Follow Mouse */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-3 h-3 rounded-full pointer-events-none"
                  style={{
                    left: `${20 + i * 12}%`,
                    top: `${30 + (i % 3) * 20}%`,
                    x: useSpring(mouseXSpring, { damping: 30 + i * 5, stiffness: 100 }),
                    y: useSpring(mouseYSpring, { damping: 30 + i * 5, stiffness: 100 }),
                    background: `radial-gradient(circle, rgba(16, 185, 129, ${0.6 - i * 0.05}) 0%, transparent 70%)`
                  }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 2 + i * 0.3,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
